import { facilitiesQuery } from "@common/api/facilities"
import { toolQuery } from "@common/api/tools"
import { Button } from "@common/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@common/components/ui/dialog"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@common/components/ui/toggle-group"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router"
import { Printer } from "lucide-react"
import { useRef, useState } from "react"
import {
  type LabelKind,
  labelKindName,
  previewZoom,
  ToolLabel,
} from "../-components/tool-label"

const KINDS: readonly LabelKind[] = ["tag", "strip"]

export const Route = createFileRoute("/_layout/tools/$toolId/label")({
  component: LabelDialog,
})

function printLabel(label: HTMLElement): void {
  const popup = window.open("", "_blank", "width=600,height=400")
  if (!popup) return
  const styles = Array.from(
    document.querySelectorAll('style, link[rel="stylesheet"]'),
    (node) => node.outerHTML,
  ).join("")
  popup.document.write(
    `<!doctype html><html><head><title>Tool label</title>${styles}<style>@page{size:auto;margin:4mm}body{margin:0;padding:4px;background:white}</style></head><body>${label.outerHTML}</body></html>`,
  )
  popup.document.close()
  popup.addEventListener("load", () => popup.print())
}

function LabelDialog(): React.ReactElement {
  const { toolId } = Route.useParams()
  const { data: tool } = useSuspenseQuery(toolQuery({ tool_id: toolId }))
  const { data: facilities } = useQuery(facilitiesQuery())
  const navigate = useNavigate()
  const router = useRouter()
  const label = useRef<HTMLDivElement>(null)
  const [kind, setKind] = useState<LabelKind>("tag")
  const site =
    facilities?.data.find((facility) => facility.id === tool.facility_id)
      ?.name ?? ""
  const { href } = router.buildLocation({
    to: "/tools/$toolId",
    params: { toolId },
  })
  const close = () =>
    navigate({ to: "/tools/$toolId", params: { toolId }, search: true })

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tool label</DialogTitle>
          <DialogDescription>
            {kind === "tag"
              ? "Prints on a thermal label. The QR code opens this tool."
              : "Wrap-around strip for small tools. The QR code opens this tool."}
          </DialogDescription>
        </DialogHeader>
        <ToggleGroup
          type="single"
          size="sm"
          value={kind}
          onValueChange={(next) => {
            const picked = KINDS.find((value) => value === next)
            if (picked) setKind(picked)
          }}
          className="w-max justify-start"
        >
          {KINDS.map((value) => (
            <ToggleGroupItem key={value} value={value}>
              {labelKindName(value)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <div className="flex justify-center rounded-lg bg-muted p-4">
          <div style={{ zoom: previewZoom(kind) }}>
            <ToolLabel
              ref={label}
              tool={tool}
              kind={kind}
              url={`${window.location.origin}${href}`}
              site={site.toUpperCase()}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => label.current && printLabel(label.current)}>
            <Printer />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
